export type MultiplierMap = {
  [projectId: string]: { [configuratorTypeId: string]: number }
}

// Constants
const STORAGE_KEY = 'pkv2_multiplier_map_v1'
const CRYPTO_ALGORITHM = 'AES-GCM'

// Optional encryption key for the multiplier map
let cryptoKey: CryptoKey | null = null

/**
 * Set encryption key for the multiplier map storage
 * When set, all stored data will be encrypted/decrypted
 */
export async function setCryptoKey(keyString: string): Promise<void> {
  try {
    const encoder = new TextEncoder()
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(keyString.padEnd(32, '0').slice(0, 32)), // Ensure 32 bytes for AES-256
      'PBKDF2',
      false,
      ['deriveKey']
    )
    
    cryptoKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('pkv2_multiplier_salt'),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: CRYPTO_ALGORITHM, length: 256 },
      false,
      ['encrypt', 'decrypt']
    )
  } catch (error) {
    console.error('Failed to set crypto key:', error)
    cryptoKey = null
  }
}

/**
 * Clear the encryption key
 */
export function clearCryptoKey(): void {
  cryptoKey = null
}

/**
 * Encrypt data using the current crypto key
 */
async function encryptData(data: string): Promise<string> {
  if (!cryptoKey) {
    throw new Error('No crypto key set')
  }
  
  const encoder = new TextEncoder()
  const dataBytes = encoder.encode(data)
  
  const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV for AES-GCM
  const encrypted = await crypto.subtle.encrypt(
    { name: CRYPTO_ALGORITHM, iv },
    cryptoKey,
    dataBytes
  )
  
  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(encrypted), iv.length)
  
  // Convert to base64 for storage
  return btoa(String.fromCharCode.apply(null, Array.from(combined)))
}

/**
 * Decrypt data using the current crypto key
 */
async function decryptData(encryptedData: string): Promise<string> {
  if (!cryptoKey) {
    throw new Error('No crypto key set')
  }
  
  // Convert from base64
  const combined = new Uint8Array(
    atob(encryptedData)
      .split('')
      .map(char => char.charCodeAt(0))
  )
  
  // Extract IV and encrypted data
  const iv = combined.slice(0, 12)
  const encrypted = combined.slice(12)
  
  const decrypted = await crypto.subtle.decrypt(
    { name: CRYPTO_ALGORITHM, iv },
    cryptoKey,
    encrypted
  )
  
  const decoder = new TextDecoder()
  return decoder.decode(decrypted)
}

/**
 * Get the multiplier map from localStorage
 * Returns empty object if not found or corrupted
 */
export function getMap(): MultiplierMap {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return {}
    }
    
    if (cryptoKey) {
      // Decrypt if crypto key is available
      // Note: This is async but we need sync API, so we return cached version
      // In practice, encryption/decryption should be done at context level
      console.warn('Crypto key is set but getMap is synchronous. Use async version.')
      return {}
    }
    
    const parsed = JSON.parse(stored)
    
    // Validate the structure
    if (typeof parsed !== 'object' || parsed === null) {
      console.warn('Invalid multiplier map structure, returning empty map')
      return {}
    }
    
    // Validate each project's multipliers
    const validatedMap: MultiplierMap = {}
    for (const [projectId, configurators] of Object.entries(parsed)) {
      if (typeof configurators === 'object' && configurators !== null) {
        validatedMap[projectId] = {}
        for (const [configuratorId, multiplier] of Object.entries(configurators as Record<string, unknown>)) {
          if (typeof multiplier === 'number' && !isNaN(multiplier) && multiplier >= 1 && multiplier <= 10) {
            validatedMap[projectId][configuratorId] = multiplier
          }
        }
      }
    }
    
    return validatedMap
  } catch (error) {
    console.error('Error reading multiplier map from localStorage:', error)
    return {}
  }
}

/**
 * Get the multiplier map from localStorage (async version for encryption support)
 */
export async function getMapAsync(): Promise<MultiplierMap> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return {}
    }
    
    let dataToparse = stored
    if (cryptoKey) {
      dataToparse = await decryptData(stored)
    }
    
    const parsed = JSON.parse(dataToparse)
    
    // Validate the structure
    if (typeof parsed !== 'object' || parsed === null) {
      console.warn('Invalid multiplier map structure, returning empty map')
      return {}
    }
    
    // Validate each project's multipliers
    const validatedMap: MultiplierMap = {}
    for (const [projectId, configurators] of Object.entries(parsed)) {
      if (typeof configurators === 'object' && configurators !== null) {
        validatedMap[projectId] = {}
        for (const [configuratorId, multiplier] of Object.entries(configurators as Record<string, unknown>)) {
          if (typeof multiplier === 'number' && !isNaN(multiplier) && multiplier >= 1 && multiplier <= 10) {
            validatedMap[projectId][configuratorId] = multiplier
          }
        }
      }
    }
    
    return validatedMap
  } catch (error) {
    console.error('Error reading multiplier map from localStorage:', error)
    return {}
  }
}

/**
 * Set the multiplier map in localStorage
 */
export function setMap(map: MultiplierMap): void {
  try {
    const dataToStore = JSON.stringify(map)
    
    if (cryptoKey) {
      // For encrypted storage, use async version
      console.warn('Crypto key is set but setMap is synchronous. Use async version.')
      return
    }
    
    localStorage.setItem(STORAGE_KEY, dataToStore)
  } catch (error) {
    console.error('Error saving multiplier map to localStorage:', error)
  }
}

/**
 * Set the multiplier map in localStorage (async version for encryption support)
 */
export async function setMapAsync(map: MultiplierMap): Promise<void> {
  try {
    const dataToStore = JSON.stringify(map)
    
    let finalData = dataToStore
    if (cryptoKey) {
      finalData = await encryptData(dataToStore)
    }
    
    localStorage.setItem(STORAGE_KEY, finalData)
  } catch (error) {
    console.error('Error saving multiplier map to localStorage:', error)
  }
}

/**
 * Clear the multiplier map from localStorage
 */
export function clear(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing multiplier map from localStorage:', error)
  }
}

/**
 * Check if localStorage is available
 */
export function isStorageAvailable(): boolean {
  try {
    const test = 'storage_test'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
} 